import { Accordion, AccordionDetails, AccordionSummary, Box, CircularProgress, Link, List, Typography } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { NoteItem } from "../components/NoteItem";
import { MediaList } from "../components/MediaList";
import { frenchEvents } from "../utils";
import { toGrampsNote, type RestEvent, type RestPerson } from "../api/grampsRest";
import { restPersonName, useNotableNotes } from "../hooks/useNotableEvents";
import { useViewRoute } from "../viewRoute";

function formatEventLabel(event: RestEvent): string {
    const type = event.type ?? '';
    const label = frenchEvents[type as keyof typeof frenchEvents] ?? type;
    if (type === 'Occupation' && event.description) {
        return `${label} : ${event.description}`;
    }
    return label;
}

export function NotableEvents() {
    const { mainId, treeName, setSideItemId } = useViewRoute();
    const { notes, isLoading } = useNotableNotes(mainId ?? null, treeName ?? null);

    if (!mainId) {
        return null;
    }

    const handlePersonClick = (person: RestPerson) => {
        if (person.gramps_id) {
            setSideItemId(person.gramps_id);
        }
    };

    return (
        <>
            <Typography variant="h6" gutterBottom>
                Notes notables, triées du plus récent au plus ancien
            </Typography>
            {isLoading && notes.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                </Box>
            )}
            {notes.map(({ key, notes: entryNotes, person, event, year, placeText, media }) => (
                <Accordion
                    key={key}
                    disableGutters
                    elevation={0}
                    sx={{ border: '1px solid', borderColor: 'divider', mb: 1, '&:before': { display: 'none' } }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 } }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {year ? `${year} — ` : ''}
                            <Link sx={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); handlePersonClick(person); }}>
                                {restPersonName(person)}
                            </Link>
                            {event ? ` — ${formatEventLabel(event)}` : ''}{placeText ? ` (${placeText})` : ''}
                        </Typography>
                        {media.length > 0 && (
                            <Box onClick={(e) => e.stopPropagation()}>
                                <MediaList medias={media} thumbSizePx={40} />
                            </Box>
                        )}
                    </AccordionSummary>
                    <AccordionDetails>
                        <List disablePadding>
                            {entryNotes.map((note) => (
                                <NoteItem key={note.handle} note={toGrampsNote(note)} />
                            ))}
                        </List>
                    </AccordionDetails>
                </Accordion>
            ))}
        </>
    );
}
